import type { RefObject } from "react";
import { useEffect, useState } from "react";

interface IProps {
  containerRef: RefObject<HTMLElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  backgroundRef: RefObject<HTMLCanvasElement | null>;
}

const LOGO_BEHIND = { start: 7.85, end: 20 };

const VERTEX = `
attribute vec2 pos;
varying vec2 uv;
void main() {
  uv = vec2(pos.x, -pos.y) * 0.5 + 0.5;
  gl_Position = vec4(pos, 0.0, 1.0);
}`;

// 영상 왼쪽: 원본, 오른쪽: 마스크
const FRAGMENT = `
precision mediump float;
uniform sampler2D video;
varying vec2 uv;
void main() {
  // 왼쪽: 색
  vec3 color = texture2D(video, vec2(uv.x * 0.5, uv.y)).rgb;
  // 오른쪽: 마스크
  float mask = texture2D(video, vec2(0.5 + uv.x * 0.5, uv.y)).r;
  // 마스크 밝기 → 투명도
  float alpha = clamp((mask - 0.03) * 1.07, 0.0, 1.0);
  gl_FragColor = vec4(color * alpha, alpha);
}`;

export default function usePersonCutout({ containerRef, videoRef, canvasRef, backgroundRef }: IProps) {
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const background = backgroundRef.current;
    const backgroundContext = background?.getContext("2d", { alpha: false });
    const snapshot = document.createElement("canvas");
    const snapshotContext = snapshot.getContext("2d", { alpha: false });
    const gl = canvas?.getContext("webgl");
    const program = gl?.createProgram();
    if (!container || !video || !canvas || !gl || !program || !background || !backgroundContext || !snapshotContext) return;

    [
      { type: gl.VERTEX_SHADER, source: VERTEX },
      { type: gl.FRAGMENT_SHADER, source: FRAGMENT },
    ].forEach(({ type, source }) => {
      const shader = gl.createShader(type);
      if (!shader) return;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      gl.attachShader(program, shader);
    });
    gl.bindAttribLocation(program, 0, "pos");
    gl.linkProgram(program);
    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    let lastTime = -1;
    let frameId = 0;
    let videoFrameId = 0;
    let active = false;
    const supportsVideoFrames = typeof video.requestVideoFrameCallback === "function";

    const draw = (source: HTMLVideoElement | HTMLImageElement, sourceWidth: number, height: number) => {
      const width = sourceWidth / 2;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        background.width = width;
        canvas.height = height;
        background.height = height;
        snapshot.width = sourceWidth;
        snapshot.height = height;
        gl.viewport(0, 0, width, height);
      }
      // Capture once: background color and masked foreground share identical pixels
      // from the same decoded frame, including during rapid head movement.
      snapshotContext.drawImage(source, 0, 0);
      backgroundContext.drawImage(snapshot, 0, 0, width, height, 0, 0, width, height);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, snapshot);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    const render = (mediaTime: number) => {
      if (video.readyState < 2 || mediaTime === lastTime) return;
      lastTime = mediaTime;
      draw(video, video.videoWidth, video.videoHeight);
      const isBehind = mediaTime >= LOGO_BEHIND.start && mediaTime < LOGO_BEHIND.end;
      container.style.setProperty("--logo-z", isBehind ? "10" : "30");
    };
    // 저전력 모드처럼 영상이 재생되지 않을 때 첫 화면
    const poster = new Image();
    poster.onload = () => {
      if (lastTime < 0) draw(poster, poster.naturalWidth, poster.naturalHeight);
    };
    poster.src = video.poster;

    const update = () => {
      if (!active) return;
      if (supportsVideoFrames) {
        videoFrameId = video.requestVideoFrameCallback((_, metadata) => {
          render(metadata.mediaTime);
          update();
        });
      } else {
        frameId = requestAnimationFrame(() => {
          render(video.currentTime);
          update();
        });
      }
    };
    const play = () => {
      if (active) return;
      active = true;
      lastTime = -1;
      render(video.currentTime);
      // 저전력 모드 등 자동재생이 막힌 경우
      video.play().catch((error) => {
        if (error.name === "NotAllowedError") setAutoplayBlocked(true);
      });
      update();
    };
    const pause = () => {
      active = false;
      cancelAnimationFrame(frameId);
      if (supportsVideoFrames) video.cancelVideoFrameCallback(videoFrameId);
      video.pause();
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) play();
      else pause();
    });
    observer.observe(container);

    const onPlaying = () => {
      setAutoplayBlocked(false);
    };
    video.addEventListener("playing", onPlaying);

    return () => {
      pause();
      observer.disconnect();
      video.removeEventListener("playing", onPlaying);
    };
  }, [containerRef, videoRef, canvasRef, backgroundRef]);

  return { autoplayBlocked };
}
